/**
 * Brian's album (coins.html v1.4, app-data.js v4.4): the 33 coins V2 has
 * switched on, in his seven sections, with his words and his "how". The
 * names, rewards and progress come from the database; the presentation is his.
 * The two held medals (Top Billing, Grand Marshal) wait (Brian, Oct 14), so
 * nothing here mentions them.
 */
import type { CaseCoin } from '@/hooks/useAchievements'

export const ALBUM_GROUPS: { key: string; name: string; why: string }[] = [
  { key: 'stories', name: 'Stories out in the world', why: 'One coin per rung. The ladder climbs steeply on purpose.' },
  { key: 'weeks', name: 'Showing up', why: 'Weeks you published. Nothing resets and nothing nags.' },
  { key: 'places', name: 'Places', why: 'Ground you cover.' },
  { key: 'emma', name: 'Emma', why: 'Questions asked, and the silo filled.' },
  { key: 'letters', name: 'Newsletter', why: 'Your own list, sent from GoGrov.' },
  { key: 'found', name: 'Found along the way', why: 'Hidden. They find you while you’re doing something else.' },
  { key: 'case', name: 'The case', why: 'For the coins themselves.' },
]

/** id → [section, words, how]. A hidden coin has no "how". */
export const BRIAN_COINS: Record<string, [string, string, string | null]> = {
  'first-harvest': ['stories', 'Your first story out in the world. Everything grows from here.', 'Publish your first story'],
  'full-bushel': ['stories', 'Ten stories out in the world. The basket is full.', 'Publish 10 stories'],
  'barrow-load': ['stories', 'Twenty-five stories hauled out. The cart is heavy.', 'Publish 25 stories'],
  'wagon-full': ['stories', 'Fifty stories. It takes a wagon now.', 'Publish 50 stories'],
  'hundred-harvest': ['stories', 'One hundred stories. A whole farm’s worth.', 'Publish 100 stories'],
  'high-summer': ['stories', 'Two hundred and fifty stories. Still going.', 'Publish 250 stories'],
  'leaf-fall': ['stories', 'Five hundred stories. The seasons keep turning.', 'Publish 500 stories'],
  'snowfall': ['stories', 'One thousand stories. Through every season.', 'Publish 1,000 stories'],
  'new-bloom': ['stories', 'Two thousand stories. Back around again.', 'Publish 2,000 stories'],
  'right-on-time': ['weeks', 'Two weeks, right on schedule.', 'Publish in 2 weeks running'],
  'stoking-the-fire': ['weeks', 'Three weeks of feeding the engine.', 'Publish in 3 weeks running'],
  'full-steam-ahead': ['weeks', 'A full month without missing a week.', 'Publish in 4 weeks running'],
  'green-thumb': ['weeks', 'One month in, and growing strong.', 'Publish every week for a month'],
  'perennial': ['weeks', 'You keep showing up. Season after season.', 'Six months active, 48 stories published'],
  'deep-roots': ['weeks', 'A full year rooted with GoGrov.', 'Your first anniversary'],
  'breaking-ground': ['places', 'New territory, freshly turned.', 'Add a second place'],
  'far-roamer': ['places', 'Past the county line. Good things live out there.', null],
  'the-aeronaut': ['places', 'Expanding your horizons. Expanding your reach.', null],
  'sage-advice': ['emma', 'The right question, asked and answered.', 'Ask Emma a question'],
  'full-silo-bronze': ['emma', 'Stocked, sealed, and ready.', 'Fill Emma’s silo once'],
  'full-silo-silver': ['emma', 'Stocked, sealed, and ready.', 'Fill Emma’s silo twice'],
  'full-silo-gold': ['emma', 'Stocked, sealed, and ready.', 'Fill Emma’s silo three times'],
  'pony-express': ['letters', 'The line is open. Ride.', 'Bring your first subscribers into your list'],
  'town-herald': ['letters', 'Word travels. Yours starts here.', 'Send your first newsletter'],
  'press-room': ['letters', 'A publishing rhythm, and a town that reads it.', 'Two newsletters a month, two months running'],
  'media-mogul': ['letters', 'From one voice to an empire of them.', 'Three newsletters a month, three months running'],
  'picky-farmer': ['found', 'Three rolls before settling. The right story is worth waiting for.', null],
  'the-gambler': ['found', 'Five rolls deep. The story is in there somewhere.', null],
  'early-bird': ['found', 'Up before the town was.', null],
  'night-owl': ['found', 'The lamp still lit long after everyone else called it a night.', null],
  'coin-collector': ['case', 'Five already. However you got them, they add up.', null],
  'treasure-hunter': ['case', 'Ten. At some point this stopped being an accident.', null],
  'clean-getaway': ['case', 'Twenty, and not one left behind.', null],
}

/** The database's groups, for a coin Brian's list doesn't name (a hidden well). */
const FROM_DB: Record<string, string> = {
  'post-ladder': 'stories', 'post-seasons': 'stories', retention: 'weeks', loyalty: 'weeks',
  coverage: 'places', emma: 'emma', media: 'letters', discovery: 'found', fortune: 'case',
}

export function albumGroup(c: CaseCoin): string {
  return BRIAN_COINS[c.id]?.[0] ?? FROM_DB[c.group_key] ?? 'found'
}
export function coinWords(c: CaseCoin): string {
  return BRIAN_COINS[c.id]?.[1] ?? c.description ?? ''
}
export function coinHow(c: CaseCoin): string | null {
  const b = BRIAN_COINS[c.id]
  return b ? b[2] : c.trigger_text
}
export const TIER: Record<string, string> = { bronze: 'Bronze', silver: 'Silver', gold: 'Gold' }

